import { handleGetProductOptionsAPI } from '@/api/storeManagement/device';
import { useAppContext } from '@/provider/contexts';
import { ProductOption } from '@/types/pages/device';
import { getInputMinLengthLimit } from '@/utils/inputUtils';
import localforage from 'localforage';
import { debounce } from 'lodash';
import { useState, useRef, useEffect } from 'react';

const useProductAutocomplete = () => {
    const { manageStoreId } = useAppContext();
    const [options, setOptions] = useState<ProductOption[]>([]);
    const [loading, setLoading] = useState(false);
    const debouncedFetchRef = useRef<ReturnType<typeof debounce> | null>(null);

    useEffect(() => {
        const fetchOptions = async (value: string) => {
            if (!manageStoreId) {
                setOptions([]);
                setLoading(false);
                return;
            }

            setLoading(true);
            try {
                const corporationId = (await localforage.getItem('corporation_id')) as number;
                const response = await handleGetProductOptionsAPI(corporationId, {
                    store_id: manageStoreId.toString(),
                    keyword: value,
                });

                let responseData: any[] = [];
                // const isSuccess = response?.data?.isSuccess ?? response?.data?.status === 200;

                if (response?.data?.data !== undefined) {
                    responseData = Array.isArray(response.data.data) ? response.data.data : [];
                } else if (Array.isArray(response?.data)) {
                    responseData = response.data;
                }

                setOptions(responseData);
            } catch (error) {
                console.error('Error fetching product options:', error);
                setOptions([]);
            } finally {
                setLoading(false);
            }
        };

        debouncedFetchRef.current = debounce(fetchOptions, 300);

        return () => {
            if (debouncedFetchRef.current) {
                debouncedFetchRef.current.cancel();
            }
        };
    }, [manageStoreId]);

    const handleInputChange = (value: string) => {
        const minLength = getInputMinLengthLimit(value);

        if (value.trim().length === 0) {
            setOptions([]);
            setLoading(false);
            if (debouncedFetchRef.current) {
                debouncedFetchRef.current.cancel();
            }
        } else if (value.length >= minLength) {
            if (debouncedFetchRef.current) {
                debouncedFetchRef.current(value);
            }
        } else {
            setOptions([]);
            setLoading(false);
            if (debouncedFetchRef.current) {
                debouncedFetchRef.current.cancel();
            }
        }
    };

    return {
        options,
        loading,
        handleInputChange,
    };
};

export default useProductAutocomplete;
